import React, { useMemo } from 'react';
import { Check, X, Circle } from 'lucide-react';
import { validatePassword } from '../../lib/passwordValidator';

/**
 * Reusable PasswordStrengthMeter component for SenritsuX.
 * Displays a 4-segment strength meter and a progressive checklist.
 * Styled with SenritsuX colors (#4d4ab6, #d9fb06, glass UI).
 */
export default function PasswordStrengthMeter({
  password = '',
  userContext = {},
  showChecklist = true,
  className = '',
}) {
  const validation = useMemo(() => {
    return validatePassword(password, userContext);
  }, [password, userContext]);

  if (!password) {
    return null;
  }

  const { score, level, requirements } = validation;

  const getSegmentColor = (segmentIndex) => {
    if (segmentIndex > score) return 'rgba(255, 255, 255, 0.15)';
    switch (score) {
      case 1:
        return '#ff6b6b'; // Weak
      case 2:
        return '#f59e0b'; // Fair
      case 3:
        return '#a3e635'; // Good
      case 4:
        return '#d9fb06'; // Strong (SenritsuX accent)
      default:
        return 'rgba(255, 255, 255, 0.15)';
    }
  };

  const checklistItems = [
    {
      id: 'length',
      label: 'At least 8 characters',
      valid: requirements.minLength,
      warning: false,
    },
    {
      id: 'uppercase',
      label: 'Uppercase letter',
      valid: requirements.hasUppercase,
      warning: false,
    },
    {
      id: 'lowercase',
      label: 'Lowercase letter',
      valid: requirements.hasLowercase,
      warning: false,
    },
    {
      id: 'number',
      label: 'Number',
      valid: requirements.hasNumber,
      warning: false,
    },
    {
      id: 'special',
      label: 'Special character',
      valid: requirements.hasSpecialChar,
      warning: false,
    },
    {
      id: 'emoji',
      label: 'No emoji',
      valid: requirements.hasNoEmoji,
      warning: requirements.hasEmoji,
    },
    {
      id: 'userinfo',
      label: "Doesn't contain email/name",
      valid: requirements.hasNoUserInfo,
      warning: requirements.userInfoCheck?.contains,
    },
    {
      id: 'common',
      label: 'Not a common password',
      valid: requirements.isNotCommon,
      warning: !requirements.isNotCommon && password.length >= 4,
    },
  ];

  return (
    <div className={`password-strength-container ${className}`.trim()} aria-live="polite">
      {/* Strength Bar + Label */}
      <div className="password-strength-header">
        <div className="password-strength-bars" role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={4}>
          {[1, 2, 3, 4].map((seg) => (
            <div
              key={seg}
              className={`password-strength-segment ${seg <= score ? 'active' : ''}`}
              style={{
                backgroundColor: getSegmentColor(seg),
              }}
            />
          ))}
        </div>
        <span
          className="password-strength-badge"
          style={{
            color: score === 4 ? '#d9fb06' : score === 3 ? '#a3e635' : score === 2 ? '#fbbf24' : '#ff6b6b',
          }}
        >
          {level}
        </span>
      </div>

      {/* Progressive Checklist */}
      {showChecklist && (
        <div className="password-requirements-grid">
          {checklistItems.map((item) => {
            let icon = <Circle size={12} className="req-icon req-pending" aria-hidden="true" />;
            let stateClass = 'pending';

            if (item.warning) {
              icon = <X size={12} className="req-icon req-failed" aria-hidden="true" />;
              stateClass = 'failed';
            } else if (item.valid) {
              icon = <Check size={12} className="req-icon req-valid" aria-hidden="true" />;
              stateClass = 'valid';
            }

            return (
              <div key={item.id} className={`password-req-item ${stateClass}`}>
                {icon}
                <span className="password-req-text">{item.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
